import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth, Hub } from 'aws-amplify';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user on mount and refresh
  useEffect(() => {
    const checkUser = async () => {
      setIsLoading(true);
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        setUser(currentUser);
      } catch (error) {
        console.debug('No authenticated user', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkUser();

    // Set up auth listener for sign-in/sign-out events
    const authListener = Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          checkUser();
          break;
        case 'signOut':
          setUser(null);
          // Clear any cached data
          localStorage.clear();
          sessionStorage.clear();
          break;
        case 'signIn_failure':
          setUser(null);
          break;
        default:
          break;
      }
    });

    // Cleanup listener on unmount
    return () => {
      authListener();
    };
  }, []);

  const signOut = async () => {
    try {
      // Clear user state immediately
      setUser(null);
      
      // Clear cached data
      localStorage.clear();
      sessionStorage.clear();
      
      // Sign out from Amplify
      await Auth.signOut({ global: true });
      
      // Force reload to clear any remaining cached state
      window.location.reload();
    } catch (error) {
      console.error('Error signing out', error);
      // Even if sign out fails, clear local state and reload
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  };

  return (
    <AuthContext.Provider value={{ user, signOut, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
